import React, { useState } from "react";
import { useForm } from "react-hook-form";
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useTranslation } from "react-i18next";
import TopBarNav from '../styled/Topbar';
import { BeatLoader } from 'react-spinners';

/* ─── animations ─── */
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
`;

/* ─── layout ─── */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  padding: 5rem 1rem 2rem;
  position: relative;
  overflow: hidden;
`;

const Orb = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 0; overflow: hidden;
  &::before, &::after {
    content: ''; position: absolute; border-radius: 50%; filter: blur(80px);
    animation: ${pulse} 6s ease-in-out infinite;
  }
  &::before { width: 400px; height: 400px; background: rgba(99,102,241,0.2); top: -8%; left: -8%; }
  &::after { width: 350px; height: 350px; background: rgba(236,72,153,0.15); bottom: -5%; right: -6%; animation-delay: 2s; }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  backdrop-filter: blur(16px);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const IntroText = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  line-height: 1.6;
  max-width: 320px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  max-width: 280px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.7rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.2s, background 0.2s;

  &::placeholder { color: rgba(255, 255, 255, 0.3); }
  &:focus { outline: none; border-color: rgba(99,102,241,0.5); background: rgba(255,255,255,0.08); }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.7rem 0;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 14px rgba(99,102,241,0.3);

  &:hover { transform: translateY(-1px); box-shadow: 0 5px 20px rgba(99,102,241,0.45); }
  &:active { transform: translateY(0); }
`;

const ErrorText = styled.p`
  font-size: 0.78rem;
  color: #fca5a5;
  margin: 0;
  text-align: center;
`;

const MessageBox = styled.div`
  text-align: center;
  color: rgba(255,255,255,0.7);
  font-size: 0.9rem;
  line-height: 1.6;
  padding: 0.5rem 0;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(30, 27, 75, 0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 10;
  border-radius: 24px;
`;

/* ─── component ─── */
const ForgotPassword = () => {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const [message, setMessage] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            await axios.post(`${process.env.REACT_APP_SERVER}/forgot-password`, { email: data.email }, { withCredentials: true });
            setIsSuccess(true);
            setMessage(t('ForgotPasswordNextText'));
        } catch (error) {
            setIsSuccess(false);
            setMessage(t('GenericErrorText'));
        }
        setIsLoading(false);
    };

    return (
        <PageWrapper>
            <Orb />
            <TopBarNav onSignInClick={() => {}} />

            <Card>
                {isLoading && <SpinnerOverlay><BeatLoader color="#a5b4fc" size={12} /></SpinnerOverlay>}

                {!isSuccess ? (
                    <>
                        <IntroText>{t('ForgotPasswordText')}</IntroText>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Input
                                inputMode="email" maxLength={70}
                                {...register("email", { required: true, pattern: /^\S+@\S+$/i })}
                                type="email"
                                placeholder={t('RegisterPageEmailText')}
                            />
                            {errors.email && <ErrorText>{t('SignupEmailValidate')}</ErrorText>}
                            <SubmitButton type="submit">{t('ContinueButton')}</SubmitButton>
                        </Form>
                    </>
                ) : null}

                {message && <MessageBox>{message}</MessageBox>}
            </Card>
        </PageWrapper>
    );
};

export default ForgotPassword;
